import { createFileRoute, Link } from "@tanstack/react-router";
import { IMAGES, BBN_PHOTOS } from "@/lib/images";
import { HeroBgImage } from "@/components/HeroBgImage";
import { BookText, ArrowRight } from "lucide-react";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";

interface Term {
  term: string;
  short: string;
  def: string;
  category: string;
}

const TERMS: Term[] = [
  { term: "TSER", short: "Total Solar Energy Rejection", def: "Andelen av solens totala energi som reflekteras eller absorberas av filmen istället för att passera in i rummet. Ju högre värde, desto bättre värmereduktion. 3M Prestige 70 ligger på 50 % TSER; exteriör silverfilm som Solar Screen Silver 70 RS når upp till 78 %.", category: "Solfilm" },
  { term: "VLT", short: "Visible Light Transmission", def: "Den andel synligt ljus som släpps igenom filmen, angiven i procent. 70 % VLT betyder att rummet upplevs nästan lika ljust som utan film.", category: "Solfilm" },
  { term: "LT", short: "Light Transmission", def: "Betyder samma sak som VLT i vissa datablad.", category: "Solfilm" },
  { term: "UV-blockering", short: "Ultraviolet rejection", def: "Andel UV-strålning (våglängd 280–380 nm) som blockeras. 3M:s solfilmer blockerar 99 % av både UV-A och UV-B och stoppar därmed i praktiken UV-driven blekning av parkett, textil och konst.", category: "Solfilm" },
  { term: "IR-reduktion", short: "Infrared rejection", def: "Andel infraröd värmestrålning som blockeras. IR är den största delen av solens värme som kommer genom fönstret.", category: "Solfilm" },
  { term: "Spektralt selektiv", short: "Spectrally selective film", def: "Solfilm som släpper igenom synligt ljus men blockerar IR och UV. Glaset behöver därför inte bli mörkt eller speglande.", category: "Solfilm" },
  { term: "Low-E", short: "Low Emissivity", def: "Beläggning eller film med låg värmeavstrålning som hjälper både att hålla värme inne på vintern och ute på sommaren.", category: "Solfilm" },
  { term: "U-värde", short: "Värmegenomgångskoefficient", def: "Hur mycket värme som leds genom glaset per kvadratmeter och kelvin. Ett lägre värde betyder att mindre värme leds genom glaset. Film kan sänka U-värdet något.", category: "Glasteknik" },
  { term: "g-värde", short: "Solar factor", def: "Den andel av solenergin som kommer in i rummet, alltså motsatsen till TSER. Ett lägre g-värde betyder bättre solskydd.", category: "Glasteknik" },
  { term: "EN 12600", short: "Pendelprov för glas", def: "Europeisk standard för pendeltest som visar hur glas klarar en stöt från en människa. Den klassar glas i 1B1, 2B2 eller 3B3 beroende på hur det beter sig vid kross. Säkerhetsfilm kan höja klassningen utan att glaset byts.", category: "Säkerhet" },
  { term: "EN 356", short: "Skydd mot manuell attack", def: "Europeisk standard som klassar glas-/film­konstruktioner P1A–P5A efter hur länge de står emot upprepade slag.", category: "Säkerhet" },
  { term: "AFS 2020:1", short: "Arbetsmiljöverkets föreskrift", def: "Föreskrift om arbetsplatsens utformning. Reglerar att glas i arbetslokaler ska vara skyddat så att personer inte skadas vid kross. Säkerhetsfilm är ett godkänt sätt att uppfylla kravet.", category: "Säkerhet" },
  { term: "BBR 8:35", short: "Boverkets byggregler", def: "Krav på skydd mot personskador vid glas över 0,6 m² i bostäder och offentliga utrymmen. Aktiveras vid om- och tillbyggnad.", category: "Säkerhet" },
  { term: "Splitterskydd", short: "Splinter protection", def: "Splitterskydd betyder att filmen håller glasbitarna på plats när rutan går sönder. Det skyddar främst mot personskador.", category: "Säkerhet" },
  { term: "IPA", short: "Impact Protection Attachment", def: "3M:s system för att förankra säkerhetsfilm mot karmen, antingen med strukturlim eller mekanisk profil. Krävs för maximalt inbrotts- och explosionsskydd.", category: "Säkerhet" },
  { term: "Mil (mått)", short: "1 mil = 25,4 μm", def: "Amerikansk måttenhet för filmtjocklek. 8 mil = ca 200 μm = 3M Ultra säkerhetsfilm.", category: "Glasteknik" },
  { term: "μm (mikrometer)", short: "1 μm = 0,001 mm", def: "Måttenhet för filmtjocklek. Solfilm är typiskt 50 μm, säkerhetsfilm 100–375 μm.", category: "Glasteknik" },
  { term: "Topcoat", short: "Yttre slittåligt skikt", def: "Filmens utåtvända ytskikt som skyddar mot repor från normal rengöring. Skadas av ammoniak och slipmedel.", category: "Underhåll" },
  { term: "Härdning", short: "Curing", def: "Tiden efter monteringen då vattnet i limmet torkar genom filmen, vanligtvis 2–4 veckor. Under denna tid kan filmen se mjölkig ut eller ha små bubblor som försvinner av sig själv.", category: "Underhåll" },
  { term: "Dichroic", short: "Dikroisk film", def: "Specialfilm som ändrar färg beroende på betraktningsvinkel. Används för dekorativa effekter på glasväggar och konst.", category: "Dekorfilm" },
  { term: "Frostfilm", short: "Dusted glass film", def: "Dekorfilm som gör glaset frostat och ogenomskinligt. Den används oftast i mötesrum, på badrumsfönster och på glasväggar.", category: "Dekorfilm" },
  { term: "One-way film", short: "Spegelfilm", def: "Film som ser ut som spegel från den ljusare sidan och genomskinlig från den mörkare. Vanlig för kontor i markplan och konferensrum.", category: "Dekorfilm" },
  { term: "Prestige-serien", short: "3M:s spektralt selektiva solfilm", def: "3M:s spektralt selektiva solfilmer (Prestige 70 invändigt samt Prestige 40 Exterior, Prestige 70 Exterior och Prestige 90 Exterior utvändigt). Filmerna har över 200 nano-lager polymer i stället för metall. De minskar värmen utan att färga glaset eller ge spegeleffekt.", category: "Produkt" },
  { term: "3M Ultra", short: "3M:s säkerhetsfilm", def: "Säkerhetsfilm med 42 mikroskopiska polyesterlager. Filmen är tunn och mycket seg samt klassad för EN 12600.", category: "Produkt" },
];

const CATEGORIES = Array.from(new Set(TERMS.map((t) => t.category)));

export const Route = createFileRoute("/ordlista")({
  head: () => ({
    meta: [
      { title: "Ordlista — fönsterfilm-termer förklarade | BBN Nordic" },
      { name: "description", content: "TSER, VLT, EN 12600, AFS 2020:1, splitterskydd, Low-E, μm och fler termer inom fönsterfilm förklarade på svenska av BBN Nordic." },
      { property: "og:title", content: "Ordlista — fönsterfilm-termer | BBN Nordic" },
      { property: "og:description", content: "24 termer inom solfilm, säkerhetsfilm och dekorfilm — förklarade på svenska." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://bbnnordic.se/ordlista" },
      { property: "og:image", content: IMAGES.solfilm },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/ordlista" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "DefinedTermSet",
        name: "Ordlista — fönsterfilm",
        url: "/ordlista",
        hasDefinedTerm: TERMS.map((t) => ({
          "@type": "DefinedTerm",
          name: t.term,
          description: t.def,
          inDefinedTermSet: "/ordlista",
        })),
      }),
    }],
  }),
  component: OrdlistaPage,
});

function OrdlistaPage() {
  return (
    <main className="bg-background">
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-16 bg-navy text-cream overflow-hidden">
        <HeroBgImage src={BBN_PHOTOS.installationReference} alt="Ordlista för fönsterfilm — BBN Nordic" />
        <div className="relative mx-auto max-w-4xl px-5 md:px-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold mb-4">
            <BookText size={14} /> Ordlista
          </div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tight">
            Fönsterfilm — ordlista
          </h1>
          <p className="mt-6 text-lg text-cream/85 max-w-2xl font-light leading-relaxed">
            Här förklarar vi orden du möter när du läser om solfilm, säkerhetsfilm och dekorfilm.
            Vill du veta mer kan du läsa vidare i våra guider och på produktsidorna.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <Link
              to="/offert"
              className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold min-h-11"
            >
              Få gratis offert <ArrowRight size={16} />
            </Link>
            <a
              href="tel:+46771502502"
              className="inline-flex items-center justify-center gap-2 text-cream/85 px-4 py-3 font-medium hover:text-gold-bright transition-colors min-h-11"
            >
              Ring 0771 502 502
            </a>
          </div>
        </div>
      </section>
      <PostHeroConversion />

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-5 md:px-10 space-y-12">
          {CATEGORIES.map((cat) => (
            <div key={cat}>
              <h2 className="font-display text-2xl md:text-3xl text-foreground mb-5 border-b border-border pb-3">
                {cat}
              </h2>
              <dl className="grid gap-4 md:grid-cols-2">
                {TERMS.filter((t) => t.category === cat).map((t) => (
                  <div key={t.term} className="glass rounded-xl p-5">
                    <dt className="flex items-baseline gap-2 mb-1.5">
                      <span className="font-display text-xl text-foreground">{t.term}</span>
                      <span className="text-xs text-muted-foreground italic">{t.short}</span>
                    </dt>
                    <dd className="text-sm text-foreground/80 leading-relaxed">{t.def}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-bbn-neutral py-12">
        <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
          <p className="text-foreground/75 mb-4">Saknar du en term?</p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 text-bbn-primary hover:text-bbn-accent font-medium"
          >
            Hör av dig så lägger vi till den <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
